import React ,{useEffect, useState}
  from "react";
import { useNavigate } from "react-router-dom";
import { Input, InputLabel } from "./Input";
import { Arrow } from "../../pages/AddSupplier/InputForm";
import { useTranslation } from "react-i18next";
import {fetchCategories} from "../../utils/dataFetcher";

const ProductForm = ({
  formData,
  suppliers,
  setFormData,
  handleSubmit,
  isEdit = false,
  isCustomProduct
}) => {
  const { t,i18n } = useTranslation("addProductsHeader");
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

   useEffect(() => {
      fetchCategories(getLangFromLocalStorage())
        .then((data) => {
          const categoriesData = data.data || data;
          console.log("Fetched categories:", categoriesData);
          setCategories(categoriesData);
        })
        .catch((err) => console.error("Error fetching categories:", err));
    }, []);

  // Debug effect to check values
  useEffect(() => {
    if (formData.type && categories.length > 0) {
      console.log("Form type:", formData.type);
      console.log("Categories:", categories);
      console.log("Selected key:", getServiceTypeKey(formData.type));
    }
  }, [formData.type, categories]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    // Handle numeric fields differently for price (allow decimals)
    if (name === "price") {
      // Allow decimal numbers, remove leading zeros but keep decimal point
      updatedValue = value.replace(/^0+(?=\d)/, "") || (value === "" ? "" : "0");
    } else if (
      name === "shipping_period" ||
      name === "quantity" ||
      name === "minimum_order"
    ) {
      // For integer fields, strip leading zeros
      updatedValue = value.replace(/^0+/, "") || "0";
    } else if (name === "type") {
      // For type selection, store the category ID
      updatedValue = value;
    }

    const newForm = { ...formData, [name]: updatedValue };
    setFormData(newForm);
    sessionStorage.setItem("productFormData", JSON.stringify(newForm));
  };

  const getServiceTypeKey = (value) => {
    if (!value || categories.length === 0) return "";

    // If value is already a number (ID), return it as string
    if (typeof value === "number" || (!isNaN(Number(value)) && Number(value) > 0)) {
      const category = categories.find((cat) => cat.id === Number(value));
      return category ? String(category.id) : "";
    }

    // If value is a string, try to find by name first, then by id
    const category = categories.find((cat) =>
      cat.name === value ||
      cat.id === Number(value) ||
      String(cat.id) === value
    );
    return category ? String(category.id) : "";
  };

  // Helper function to get category name for display
  const getCategoryName = (value) => {
    if (!value || categories.length === 0) return "";

    const category = categories.find((cat) =>
      cat.name === value ||
      cat.id === Number(value) ||
      String(cat.id) === value
    );
    return category ? category.name : "";
  };
  // SKU value - ensure it's always a string
  const skuValue = String(formData.sku || "").trim();

  // other validations - ensure all values are strings for proper validation
  const isNameInvalid =
    formData.name &&
    formData.name.length > 0 &&
    formData.name.trim().length < 3;
  const isPriceInvalid =
    formData.price &&
    String(formData.price).length > 0 &&
    (Number(formData.price) < 0 || isNaN(Number(formData.price)));

  const isShippingPeriodInvalid =
    formData.shipping_period &&
    String(formData.shipping_period).length > 0 &&
    Number(formData.shipping_period) < 1;
  const isMinimumOrderInvalid =
    formData.minimum_order &&
    String(formData.minimum_order).length > 0 &&
    Number(formData.minimum_order) < 1;
  const isStockInvalid =
    formData.quantity &&
    String(formData.quantity).length > 0 &&
    Number(formData.quantity) < 1;
  const isDescriptionInvalid =
    formData.description &&
    formData.description.length > 0 &&
    formData.description.trim().length < 10;

    // valdideate arabic name and description
  const isNameArInvalid =
    formData.name_ar &&
    formData.name_ar.length > 0 &&
    formData.name_ar.trim().length < 3;

  const isDescriptionArInvalid =
    formData.description_ar &&
    formData.description_ar.length > 0 &&
    formData.description_ar.trim().length < 10;

  const isOfferTypeInvalid =
    formData.offer_type &&
    formData.offer_type.length > 0 &&
    formData.offer_type.trim().length < 3;

  // overall form valid? - Updated to use categories and handle custom products
  const localFormValid =
    skuValue.length > 0 && // Only check if SKU is not empty
    formData.name &&
    formData.name.trim().length >= 3 &&
    formData.name_ar &&
    formData.name_ar.trim().length >= 3 &&
    formData.description_ar &&
    formData.description_ar.trim().length >= 10 &&
    formData.supplier_id &&
    formData.type &&
    getServiceTypeKey(formData.type) !== "" && // Validate the type exists in categories
    formData.offer_type && // Require offer type selection
    formData.description &&
    formData.description.trim().length >= 10 &&
    !isOfferTypeInvalid &&
    // For non-custom products, require these additional fields
    (isCustomProduct || (
      formData.price &&
      Number(formData.price) >= 0 && // Allow zero and positive values including decimals
      !isNaN(Number(formData.price)) &&
      formData.shipping_period &&
      Number(formData.shipping_period) >= 1 &&
      formData.minimum_order &&
      Number(formData.minimum_order) >= 1 &&
      formData.quantity &&
      Number(formData.quantity) >= 1
    ));

  return (
    <form className="form-container"

    style={{flexDirection:"column"}}
    onSubmit={handleSubmit}>
      {/* Two Column Layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

        {/* Left Column */}
        <div className="space-y-6">
          {/* Product Name (English) */}
          <div className="relative mb-6">
            <InputLabel>{t("labels.name")}</InputLabel>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t("placeholders.name")}
              required
              inValid={isNameInvalid}
            />
            {isNameInvalid && (
              <p className="absolute top-20 text-red-500 text-[12px]">
                {t("validation.nameInvalid")}
              </p>
            )}
          </div>

          {/* Description (English) */}
          <div className="relative mb-6">
            <InputLabel>{t("labels.description")}</InputLabel>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={6}
              maxLength={500}
              required
              placeholder={t("placeholders.description")}
              className="p-3 w-full text-lg bg-white rounded-lg border border-gray-200 resize-none focus:border-primary focus:outline-none"
            />
            <div className="absolute bottom-2 right-2 text-text-medium text-[12px]">
              {500 - formData.description.length}
            </div>
            {isDescriptionInvalid && (
              <p className="absolute bottom-[-18px] text-red-500 text-[12px]">
                {t("validation.descriptionInvalid")}
              </p>
            )}
          </div>

          {/* Supplier */}
          <div className="relative mb-6">
            <InputLabel>{t("labels.supplier")}</InputLabel>
            <div className="relative">
              <select
                name="supplier_id"
                value={formData.supplier_id}
                onChange={handleChange}
                required
                className="px-3 py-2 w-full h-12 text-lg rounded-lg border border-gray-200 appearance-none focus:border-primary focus:outline-none"
              >
                <option value="">{t("labels.selectSupplier")}</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <Arrow />
            </div>
          </div>

          {/* Price - Hidden for custom products */}
          {!isCustomProduct && (
            <div className="relative mb-6">
              <InputLabel>{t("labels.price")}
                {formData.offer_type === "rent" ? t("labels.perHour"):''}
              </InputLabel>
              <Input
                type="text"
                name="price"
                min={0}
                step="any"
                pattern="^\d*\.?\d*$"
                value={formData.price}
                onChange={handleChange}
                onInput={(e) => {
                  const value = e.target.value;
                  if (value && !/^\d*\.?\d*$/.test(value)) {
                    e.target.setCustomValidity("Please enter a valid number");
                  } else {
                    e.target.setCustomValidity("");
                  }
                }}
                placeholder={t("placeholders.price")}
                required
                inValid={isPriceInvalid}
              />
              {isPriceInvalid && (
                <p className="absolute top-20 text-red-500 text-[12px]">
                  {t("validation.priceInvalid")}
                </p>
              )}
            </div>
          )}

          {/* SKU Number */}
          <div className="relative mb-6">
            <InputLabel>{t("labels.skuNumber")}</InputLabel>
            <Input
              type="text"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              placeholder={t("placeholders.skuNumber")}
              required
            />
          </div>

          {/* Minimum Order - Hidden for custom products */}
          {!isCustomProduct && (
            <div className="relative mb-6">
              <InputLabel>{t("labels.minimumOrder")}</InputLabel>
              <Input
                type="number"
                name="minimum_order"
                min={1}
                value={formData.minimum_order}
                onChange={handleChange}
                placeholder={t("placeholders.minimumOrder")}
                required
                inValid={isMinimumOrderInvalid}
              />
              {isMinimumOrderInvalid && (
                <p className="absolute top-20 text-red-500 text-[12px]">
                  {t("validation.minimumOrderInvalid")}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Product Name (Arabic) */}
          <div className="relative mb-6">
            <InputLabel>{t("labels.name_ar")}</InputLabel>
            <Input
              type="text"
              name="name_ar"
              value={formData.name_ar}
              onChange={handleChange}
              placeholder={t("placeholders.name_ar")}
              required
              inValid={isNameArInvalid}
            />
            {isNameArInvalid && (
              <p className="absolute top-20 text-red-500 text-[12px]">
                {t("validation.nameArInvalid")}
              </p>
            )}
          </div>

          {/* Description (Arabic) */}
          <div className="relative mb-6">
            <InputLabel>{t("labels.description_ar")}</InputLabel>
            <textarea
              name="description_ar"
              value={formData.description_ar}
              onChange={handleChange}
              rows={6}
              maxLength={500}
              placeholder={t("placeholders.description_ar")}
              style={{ direction: "rtl" }}
              required
              className="p-3 w-full text-lg bg-white rounded-lg border border-gray-200 resize-none focus:border-primary focus:outline-none"
            />
            <div className="absolute bottom-2 right-2 text-text-medium text-[12px]">
              {500 - formData.description_ar.length}
            </div>
            {isDescriptionArInvalid && (
              <p className="absolute bottom-[-18px] text-red-500 text-[12px]">
                {t("validation.descriptionArInvalid")}
              </p>
            )}
          </div>

          {/* Product Type */}
          <div className="relative mb-6">
            <InputLabel>{t("labels.type")}</InputLabel>
            <div className="relative">
              <select
                name="type"
                value={getServiceTypeKey(formData.type) || ""}
                onChange={handleChange}
                required
                className="px-3 py-2 w-full h-12 text-lg rounded-lg border border-gray-200 appearance-none focus:border-primary focus:outline-none"
              >
                <option value="">{t("labels.selectType")}</option>
                {categories.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <Arrow />
            </div>
          </div>

          {/* Shipping Period - Hidden for custom products */}
          {!isCustomProduct && (
            <div className="relative mb-6">
              <InputLabel>{t("labels.shippingPeriod")}</InputLabel>
              <Input
                type="number"
                name="shipping_period"
                min={1}
                value={formData.shipping_period}
                onChange={handleChange}
                placeholder={t("placeholders.shippingPeriod")}
                required
                inValid={isShippingPeriodInvalid}
              />
              {isShippingPeriodInvalid && (
                <p className="absolute top-20 text-red-500 text-[12px]">
                  {t("validation.shippingPeriodInvalid")}
                </p>
              )}
            </div>
          )}

          {/* Stock - Hidden for custom products */}
          {!isCustomProduct && (
            <div className="relative mb-6">
              <InputLabel>{t("labels.stock")}</InputLabel>
              <Input
                type="number"
                name="quantity"
                min={1}
                value={formData.quantity}
                onChange={handleChange}
                placeholder={t("placeholders.stock")}
                required
                inValid={isStockInvalid}
              />
              {isStockInvalid && (
                <p className="absolute top-20 text-red-500 text-[12px]">
                  {t("validation.stockInvalid")}
                </p>
              )}
            </div>
          )}

          {/* Offer Type */}
          <div className="relative mb-6">
            <InputLabel>{t("labels.offerType")}</InputLabel>
            <div className="flex gap-2 w-full">
              <label
                className={`w-full flex flex-row items-center cursor-pointer px-2 py-3 gap-2 bg-white border rounded-lg transition-colors ${
                  formData.offer_type === "buy"
                    ? "border-[#5507CF] bg-[#F4F1FF]"
                    : "border-[#EDEDED]"
                }`}
                style={{
                  boxSizing: 'border-box',
                  padding: '12px 8px',
                  gap: '8px'
                }}
              >
                <input
                  type="radio"
                  name="offer_type"
                  value="buy"
                  checked={formData.offer_type === "buy"}
                  onChange={handleChange}
                  className="text-[#5507CF] focus:ring-[#5507CF]"
                />
                <span className="text-lg">{t("labels.forSale")}</span>
              </label>
              <label
                className={`w-full flex flex-row items-center cursor-pointer px-2 py-3 gap-2 bg-white border rounded-lg transition-colors ${
                  formData.offer_type === "rent"
                    ? "border-[#5507CF] bg-[#F4F1FF]"
                    : "border-[#EDEDED]"
                }`}
                style={{
                  boxSizing: 'border-box',
                  padding: '12px 8px',
                  gap: '8px'
                }}
              >
                <input
                  type="radio"
                  name="offer_type"
                  value="rent"
                  checked={formData.offer_type === "rent"}
                  onChange={handleChange}
                  className="text-[#5507CF] focus:ring-[#5507CF]"
                />
                <span className="text-lg">{t("labels.forRent")}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Buttons - Bottom Right */}
      <div className="flex gap-3 justify-end mt-8">
        <button
          type="button"
          className="w-[129px] bg-[#F4F1FF] text-[#5507CF] border border-gray-200 rounded-lg py-3 font-semibold"
          onClick={() => {
            sessionStorage.clear();
            navigate("/products", { replace: true });
          }}
        >
          {t("buttons.cancel")}
        </button>
        <button
          type="submit"
          disabled={!localFormValid}
          className={`w-[226px] py-3 font-semibold rounded-lg text-white transition ${
            !localFormValid ? "bg-[#888]" : "bg-[#6609F8]"
          }`}
        >
          {isEdit ? t("buttons.save") : t("buttons.add")}
        </button>
      </div>
    </form>
  );
};

export default ProductForm;
